import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type {
  Occupancy,
  Override,
  Period,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type {
  MinLosOverride,
  OverbookingOverride,
} from 'lumen/models/override';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { RestrictionOverridePayload } from 'lumen/services/repositories/restriction-action';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  DAY_OVERRIDE_MODAL_TAB_IDS,
  type DayOverrideModalTabIds,
  MIN_LOS_DEFAULT_VALUE,
  MIN_LOS_NO_RESTRICTION_VALUE,
  MIN_LOS_RESTRICTION_TYPE,
  type MinLosRestrictionType,
  OVERRIDE_TYPES,
  RESTRICTION_MODEL_CONTEXTS,
  RESTRICTION_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type {
  MinimalRestrictionAction,
  RestrictionActionValue,
} from 'lumen/utils/constants/restriction-actions';
import {
  calculateTotalRoomsForSale,
  getOverrideVariant,
} from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';
import {
  formatMinLosValue,
  formatOverbookingValue,
} from 'lumen/utils/pricing-assistant/restriction-display-utils';

import styles from './day-override-modal.module.css';
import OverbookingPanel from './overbooking-panel';
import Sidebar, { type SidebarItem } from './sidebar';
import type { StayRestrictionsAccordionState } from './stay-restrictions-accordion';
import StayRestrictionsPanel from './stay-restrictions-panel';

interface HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature {
  Args: {
    onCloseModal: () => void;
    period: Period;
  };
  Blocks: {
    default: [];
  };
}

interface SendRestrictionConfig {
  existingOverride: MinLosOverride | OverbookingOverride | null | undefined;
  getRestrictionValues: () => RestrictionActionValue[] | null;
  overrideRemoved: boolean;
  overrideType: RestrictionOverridePayload['type'];
  overrideValue: number | null;
}

export default class DayOverrideModal extends Component<HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service('restrictions/data-request-service')
  declare restrictionsDataRequestService: Services['restrictions/data-request-service'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeTabId: DayOverrideModalTabIds =
    DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING;
  @tracked hotelMinLos: number | null = null;
  @tracked hotelMinLosAutomationOn: boolean = false;
  @tracked hotelMinLosRestrictionType: MinLosRestrictionType =
    MIN_LOS_RESTRICTION_TYPE.MIN_LOS;
  @tracked hotelMinLosTouched = false;
  @tracked overbooking: number | null = null;
  @tracked overbookingAutomationOn: boolean = false;
  @tracked overbookingTouched = false;

  /* Snapshot at modal open — read live it would flip after the delete + re-request,
     breaking hotelMinLosOverrideRemoved mid-send. */
  initiallyHadHotelMinLosOverride: boolean = false;
  /* Snapshot at modal open — read live it would flip after the delete + re-request,
     breaking overbookingOverrideRemoved mid-send. */
  initiallyHadOverbookingOverride: boolean = false;

  get formattedHotelMinLosValue(): string {
    return formatMinLosValue({
      intl: this.intl,
      value: this.args.period.effectiveMinLos,
    });
  }

  get formattedOverbookingValue(): number | string {
    return formatOverbookingValue({
      intl: this.intl,
      value: this.args.period.effectiveOverbooking,
    });
  }

  get hasRestrictionsTabToggle(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.DAY_OVERRIDE_MODAL_RESTRICTIONS_TAB,
    );
  }

  get hotelLevelRestriction(): Restriction | undefined {
    return this.args.period.hotelLevelRestriction;
  }

  get hotelLevelRestrictionRecommendation():
    | RestrictionRecommendation
    | undefined {
    return this.args.period.hotelLevelRestrictionRecommendation;
  }

  get hotelMinLosErrorMessage(): string | null {
    if (!this.hotelMinLosTouched || this.hotelMinLosAutomationOn) {
      return null;
    }
    if (this.hotelMinLosRestrictionType !== MIN_LOS_RESTRICTION_TYPE.MIN_LOS) {
      return null;
    }
    if (this.hotelMinLos === null) {
      return this.intl.t('general.required');
    }
    if (this.hotelMinLos <= 1) {
      return this.intl.t('pricing_manager.restrictions.min_los_min_error');
    }
    return null;
  }

  get hotelMinLosOverrideRemoved(): boolean {
    return this.hotelMinLosAutomationOn && this.initiallyHadHotelMinLosOverride;
  }

  get hotelMinLosState(): StayRestrictionsAccordionState {
    return {
      automationOn: this.hotelMinLosAutomationOn,
      errorMessage: this.hotelMinLosErrorMessage,
      formattedValue: this.formattedHotelMinLosValue,
      hasOverride: this.args.period.hasMinLosOverride,
      onBlur: (): void => this.onHotelMinLosBlur(),
      onTypeChange: (event: Event): void => this.onHotelMinLosTypeChange(event),
      restrictionType: this.hotelMinLosRestrictionType,
      toggleAutomation: (event: Event): void =>
        this.toggleHotelMinLosAutomation(event),
      touched: this.hotelMinLosTouched,
      updateValue: (value: number | null): void =>
        this.updateHotelMinLos(value),
      value: this.hotelMinLos,
    };
  }

  get isOverbooked(): boolean {
    return (
      typeof this.physicalRoomsAvailable === 'number' &&
      this.physicalRoomsAvailable < 0
    );
  }

  get isSending(): boolean {
    const isActionInProgress =
      this.args.period.hasOverbookingActionInProgress ||
      this.args.period.hasMinLosActionInProgress;
    const isSending =
      isActionInProgress ||
      this.restrictionsDataRequestService.createRestrictionActionTask.isRunning;
    const isDeletingOverride =
      this.pricingAssistantDataRequestService.deleteOverridesTask.isRunning;
    const isReRequestingData =
      this.pricingAssistantDataRequestService.restrictionsRequestTask
        .isRunning ||
      this.pricingAssistantDataRequestService
        .restrictionsRecommendationsRequestTask.isRunning;

    return isSending || isDeletingOverride || isReRequestingData;
  }

  get modalTitle(): string {
    const carate = this.args.period.carates?.[0];
    const day = carate?.period.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );

    return `${this.intl.t('general.edit')} ${day}, ${this.intl.t('pricing_assistant.all_rooms')}`;
  }

  get occupancyData(): Occupancy | undefined {
    return this.args.period.day.occupancy;
  }

  /* True when the user has toggled automation ON from an initial OFF state,
     meaning the existing override will be deleted on Send. */
  get overbookingOverrideRemoved(): boolean {
    return this.overbookingAutomationOn && this.initiallyHadOverbookingOverride;
  }

  private get pendingConfigs(): SendRestrictionConfig[] {
    const configs: SendRestrictionConfig[] = [];

    if (
      this.showOverbookingTab &&
      (!this.overbookingAutomationOn || this.overbookingOverrideRemoved)
    ) {
      configs.push({
        existingOverride: this.hotelLevelRestriction?.overbookingOverride,
        overrideRemoved: this.overbookingOverrideRemoved,
        overrideValue: this.overbooking,
        overrideType: OVERRIDE_TYPES.OVERBOOKING,
        getRestrictionValues: () => this.getOverbookingRestrictionValues(),
      });
    }

    if (
      this.showStayRestrictionsTab &&
      (!this.hotelMinLosAutomationOn || this.hotelMinLosOverrideRemoved)
    ) {
      const overrideValue =
        this.hotelMinLosRestrictionType ===
        MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION
          ? MIN_LOS_NO_RESTRICTION_VALUE
          : this.hotelMinLos;
      configs.push({
        existingOverride: this.hotelLevelRestriction?.minLosOverride,
        overrideRemoved: this.hotelMinLosOverrideRemoved,
        overrideValue,
        overrideType: OVERRIDE_TYPES.MIN_LOS,
        getRestrictionValues: () => this.getHotelMinLosRestrictionValues(),
      });
    }

    return configs;
  }

  get physicalRoomsAvailable(): number | string {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return this.occupancyData.availableRooms;
    }

    return this.intl.t('general.missing');
  }

  get physicalRoomsAvailableLabel(): string {
    if (
      this.occupancyData &&
      this.occupancyData.inventoryExcludingOOORooms !== null
    ) {
      return this.intl.t(
        'pricing_manager.overbooking.physical_rooms_available_tooltip',
        {
          outOfOrder: this.occupancyData.roomsOutOfOrder,
        },
      );
    }

    return this.intl.t(
      'pricing_manager.overbooking.physical_rooms_available_tooltip',
      {
        outOfOrder: this.intl.t('general.missing'),
      },
    );
  }

  get roomsForSaleValue(): string | number {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return calculateTotalRoomsForSale(
        this.occupancyData.availableRooms,
        this.overbooking ?? 0, // Treat empty overbooking (null) as 0 for calculation purposes
      );
    }

    return this.intl.t('general.missing');
  }

  get sendButtonLabel(): string {
    const label = this.intl.t('pricing_assistant.label.push');
    const count = this.pendingConfigs.length;
    return count > 0 ? `${label} (${count})` : label;
  }

  get showOverbookingException(): boolean {
    return this.args.period.shouldShowOverbookingException;
  }

  get showOverbookingOverride(): boolean {
    return (
      this.args.period.hasOverbookingOverride && !this.showOverbookingException
    );
  }

  get showOverbookingTab(): boolean {
    return (
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_OVERBOOKING_AUTOPILOT,
      ) && this.access.hasPricingAssistantOverbookingRestrictionsFeature
    );
  }

  get showStayRestrictionsTab(): boolean {
    return (
      this.hasRestrictionsTabToggle &&
      this.access.hasPricingAssistantMinLosRestrictionsFeature
    );
  }

  get sidebarItems(): SidebarItem[] {
    const items: SidebarItem[] = [];

    if (this.showOverbookingTab) {
      items.push({
        id: DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING,
        hasException: this.showOverbookingException,
        hasOverride: this.showOverbookingOverride,
        label: this.intl.t('pricing_manager.overbooking.sidebar_label'),
        overrideVariant: getOverrideVariant(OVERRIDE_TYPES.OVERBOOKING),
      });
    }

    if (this.showStayRestrictionsTab) {
      items.push({
        id: DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS,
        hasOverride: this.args.period.hasMinLosOverride,
        label: this.intl.t('pricing_manager.restrictions.sidebar_label'),
        overrideVariant: getOverrideVariant(OVERRIDE_TYPES.MIN_LOS),
      });
    }

    return items;
  }

  constructor(
    owner: Owner,
    args: HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature['Args'],
  ) {
    super(owner, args);
    this.overbooking = this.args.period.effectiveOverbookingWithOverride;
    // Workaround: BE still sends the failed override value when a push fails, showing an exception instead.
    // Treat the override as absent so overbookingOverrideRemoved stays correct if the exception clears before send.
    // Remove once COA-3687 is resolved (tracked in COA-3693).
    if (this.showOverbookingException) {
      this.initiallyHadOverbookingOverride = false;
    } else {
      this.initiallyHadOverbookingOverride =
        this.args.period.hasOverbookingOverride;
    }
    this.overbookingAutomationOn =
      this.args.period.hotelLevelRestriction?.overbookingRestrictionValueObject
        ?.autopilotEnabled ?? false;
    this.initiallyHadHotelMinLosOverride = this.args.period.hasMinLosOverride;
    this.hotelMinLosAutomationOn =
      this.args.period.hotelLevelRestriction?.minLosRestrictionValueObject
        ?.autopilotEnabled ?? false;
    this.resetHotelMinLosState();
    this.activeTabId =
      this.sidebarItems[0]?.id ?? DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING;
  }

  private getHotelMinLosRestrictionValues(): RestrictionActionValue[] | null {
    if (this.hotelMinLosOverrideRemoved) {
      const recommendationValue =
        this.hotelLevelRestrictionRecommendation?.minLosValue;
      return recommendationValue != null
        ? [{ type: RESTRICTION_TYPES.MIN_LOS, value: recommendationValue }]
        : null;
    }

    if (
      this.hotelMinLosRestrictionType ===
      MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION
    ) {
      return [
        {
          type: RESTRICTION_TYPES.MIN_LOS,
          value: MIN_LOS_NO_RESTRICTION_VALUE,
        },
      ];
    }

    if (this.hotelMinLos == null) {
      return null;
    }

    return [{ type: RESTRICTION_TYPES.MIN_LOS, value: this.hotelMinLos }];
  }

  private getOverbookingRestrictionValues(): RestrictionActionValue[] | null {
    if (this.overbookingOverrideRemoved) {
      const recommendationValue =
        this.hotelLevelRestrictionRecommendation?.overbookingValue;
      return recommendationValue != null
        ? [{ type: RESTRICTION_TYPES.OVERBOOKING, value: recommendationValue }]
        : null;
    }

    if (this.overbooking == null) {
      return null;
    }

    return [{ type: RESTRICTION_TYPES.OVERBOOKING, value: this.overbooking }];
  }

  private resetHotelMinLosState(): void {
    if (this.hotelMinLosAutomationOn) {
      return;
    }

    const overrideValue = this.hotelLevelRestriction?.minLosOverride?.value;

    if (overrideValue === MIN_LOS_NO_RESTRICTION_VALUE) {
      this.hotelMinLos = MIN_LOS_NO_RESTRICTION_VALUE;
      this.hotelMinLosRestrictionType = MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION;
    } else {
      this.hotelMinLos = overrideValue ?? MIN_LOS_DEFAULT_VALUE;
      this.hotelMinLosRestrictionType = MIN_LOS_RESTRICTION_TYPE.MIN_LOS;
    }
  }

  private async sendAllPendingOverrides(
    configs: SendRestrictionConfig[],
  ): Promise<'success' | 'error' | null> {
    const formattedDate = this.args.period.fromDate.format('YYYY-MM-DD');
    const hotelId = this.hotelAndMarketService.hotel.id!;
    const hasRemovals = configs.some((c) => c.overrideRemoved);

    const overrideIdsToDelete = configs
      .filter((c) => c.overrideRemoved && c.existingOverride)
      .map((c) => c.existingOverride!.id);

    if (overrideIdsToDelete.length > 0) {
      const result =
        await this.pricingAssistantDataRequestService.deleteOverridesTask.perform(
          overrideIdsToDelete,
        );
      if (!result?.isSuccess) {
        return 'error';
      }
    }

    if (hasRemovals) {
      /* Pre-unload so stale records don't persist if absent from the re-request response */
      [...this.args.period.restrictions].forEach((r) =>
        this.store.unloadRecord(r),
      );
      [...this.args.period.restrictionRecommendations].forEach((r) =>
        this.store.unloadRecord(r),
      );

      await Promise.all([
        this.pricingAssistantDataRequestService.restrictionsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
        this.pricingAssistantDataRequestService.restrictionsRecommendationsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
      ]);
    }

    const overrides: RestrictionOverridePayload[] = [];
    for (const config of configs) {
      const shouldSendOverride = !config.overrideRemoved;
      if (shouldSendOverride && config.overrideValue !== null) {
        overrides.push({
          subscription_id: parseInt(hotelId, 10),
          from_date: formattedDate,
          value: config.overrideValue,
          type: config.overrideType,
          context: RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
        });
      }
    }

    this.restrictionsDataRequestService.overrideCallbackOnPushFinished = async (
      day: string,
      subscriptionId: string,
    ): Promise<void> => {
      this.store
        .peekAll<Override>('override')
        .filter((o: Override) => o.fromDate.format('YYYY-MM-DD') === day)
        .forEach((o: Override) => this.store.unloadRecord(o));

      await this.pricingAssistantDataRequestService.requestOverridesTask.perform(
        subscriptionId,
        day,
        day,
      );
    };

    /* getRestrictionValues called after the potential re-request so it reads fresh recommendation data */
    const restrictionValuesToSend = configs.flatMap(
      (config) => config.getRestrictionValues() ?? [],
    );

    /* Empty when no config yielded a value to send: a removal whose re-request returned no
       recommendation, or a send with no value entered. */
    if (restrictionValuesToSend.length === 0) {
      return hasRemovals ? 'success' : null;
    }

    const restrictionData: MinimalRestrictionAction = {
      from_date: formattedDate,
      values: restrictionValuesToSend,
    };

    try {
      await this.restrictionsDataRequestService.sendRestriction(
        restrictionData,
        RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
        overrides,
      );

      return 'success';
    } catch {
      return 'error';
    }
  }

  @action
  onCloseModal(): void {
    this.args.onCloseModal();
  }

  @action
  onHotelMinLosBlur(): void {
    this.hotelMinLosTouched = true;
  }

  @action
  onHotelMinLosTypeChange(event: Event): void {
    const type = (event.target as HTMLInputElement)
      .value as MinLosRestrictionType;
    /* hotelMinLos is intentionally not updated — the number input value is preserved
       so switching back to MIN_LOS restores whatever the user had typed before. */
    this.hotelMinLosRestrictionType = type;
  }

  @action
  onOverbookingBlur(): void {
    this.overbookingTouched = true;
  }

  @action
  async onSend(): Promise<void> {
    if (this.isSending) {
      return;
    }

    const configs = this.pendingConfigs;

    if (configs.length === 0) {
      this.onCloseModal();
      return;
    }

    const hasOverbooking = configs.some(
      (c) => c.overrideType === OVERRIDE_TYPES.OVERBOOKING,
    );
    const hasMinLos = configs.some(
      (c) => c.overrideType === OVERRIDE_TYPES.MIN_LOS,
    );

    if (hasOverbooking) {
      this.overbookingTouched = true;
    }
    if (hasMinLos) {
      this.hotelMinLosTouched = true;
    }

    if (
      hasOverbooking &&
      !this.overbookingAutomationOn &&
      this.overbooking === null
    ) {
      return;
    }
    if (hasMinLos && this.hotelMinLosErrorMessage !== null) {
      return;
    }

    for (const config of configs) {
      this.usageAnalytics.track(
        config.overrideRemoved
          ? 'Autopilot Override Disabled'
          : 'Autopilot Override Enabled',
        {
          eventTrigger: 'day-override-modal',
          product: 'pricing-manager',
          overrideType:
            config.overrideType === OVERRIDE_TYPES.MIN_LOS
              ? 'min_los'
              : 'overbooking',
        },
      );
    }

    const outcome = await this.sendAllPendingOverrides(configs);

    if (outcome === 'success') {
      this.toast.success(this.intl.t('general.changes_saved'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    } else if (outcome === 'error') {
      this.toast.error(this.intl.t('general.error_short'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    }

    this.onCloseModal();
  }

  @action
  selectTab(id: DayOverrideModalTabIds): void {
    this.activeTabId = id;
  }

  @action
  toggleHotelMinLosAutomation(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.hotelMinLosAutomationOn = checked;
    this.resetHotelMinLosState();

    if (!checked) {
      this.hotelMinLosTouched = false;
    }
  }

  @action
  toggleOverbookingAutomation(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.overbookingAutomationOn = checked;

    if (checked && this.initiallyHadOverbookingOverride) {
      // Turning automation ON when override exists — show recommendation value
      this.overbooking = this.args.period.effectiveOverbooking;
    } else if (!checked) {
      // Turning automation OFF — restore the value from when the modal opened
      this.overbooking = this.args.period.effectiveOverbookingWithOverride;
      this.overbookingTouched = false;
    }
  }

  @action
  updateHotelMinLos(value: number | null): void {
    this.hotelMinLos = value;
  }

  @action
  updateOverbooking(value: number | null): void {
    this.overbooking = value;
  }

  <template>
    <PrismModal
      @onClose={{this.onCloseModal}}
      @title={{this.modalTitle}}
      class={{styles.modal}}
    >
      <:default>
        <div class={{styles.body}}>
          <div class="text-300--regular">
            <Sidebar
              @activeTabId={{this.activeTabId}}
              @items={{this.sidebarItems}}
              @onSelect={{this.selectTab}}
            />
          </div>
          <div>
            {{#if (eq this.activeTabId DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING)}}
              <OverbookingPanel
                @automationOn={{this.overbookingAutomationOn}}
                @effectiveOverbookingWithOverride={{@period.effectiveOverbookingWithOverride}}
                @failedOverbookingValue={{@period.failedOverbookingValue}}
                @formattedOverbooking={{this.formattedOverbookingValue}}
                @hasOverbookingOverride={{this.showOverbookingOverride}}
                @isOverbooked={{this.isOverbooked}}
                @isSending={{this.isSending}}
                @isTouched={{this.overbookingTouched}}
                @onOverbookingBlur={{this.onOverbookingBlur}}
                @onToggleAutomation={{this.toggleOverbookingAutomation}}
                @onUpdateOverbooking={{this.updateOverbooking}}
                @overbooking={{this.overbooking}}
                @physicalRoomsAvailable={{this.physicalRoomsAvailable}}
                @physicalRoomsAvailableLabel={{this.physicalRoomsAvailableLabel}}
                @roomsForSaleValue={{this.roomsForSaleValue}}
                @showOverbookingException={{this.showOverbookingException}}
              />
            {{else if
              (eq this.activeTabId DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS)
            }}
              <StayRestrictionsPanel @hotelState={{this.hotelMinLosState}} />
            {{/if}}
          </div>
        </div>
      </:default>
      <:footer>
        <div
          class="mx-500 flex h-full flex-row items-center justify-end gap-200"
        >
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{this.onCloseModal}}
          />
          <PrismButton
            @buttonType="primary"
            @iconBefore="send"
            @isLoading={{this.isSending}}
            @label={{this.sendButtonLabel}}
            @onClick={{this.onSend}}
            data-test-id="day-override-send-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
